import axios from 'axios'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

class CheckoutService {
  async resolveHotelCheckoutFlow(payload) {
    try {
      const response = await api.post('/checkout/resolve-hotel-checkout-flow', payload);
      return response.data;
    } catch (error) {
      console.error('❌ resolveHotelCheckoutFlow error:', error);
      throw error;
    }
  }

  async createHotelPaymentIntent(payload) {
    try {
      const response = await api.post('/checkout/create-hotel-payment-intent', payload);
      return response.data;
    } catch (error) {
      console.error('❌ createHotelPaymentIntent error:', error);
      throw error;
    }
  }

  async validateQaStripeMinPromo({ checkoutType, qaStripeMinPromoCode }) {
    try {
      const response = await api.post('/checkout/validate-qa-stripe-min-promo', {
        checkoutType,
        qaStripeMinPromoCode
      })
      return response.data
    } catch (error) {
      console.error('validateQaStripeMinPromo error:', error)
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        null
      if (backendMessage) {
        throw new Error(backendMessage)
      }
      throw error
    }
  }

  async processFinalCheckout(checkoutData) {

    try {
      // Send the checkout data directly as the request body
      const response = await api.post('/checkout/process-final-checkout', checkoutData);

      return response;

    } catch (error) {
      console.error('❌ Checkout error:', error);

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        null
      if (backendMessage) {
        throw new Error(backendMessage)
      }
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      if (error.response?.status === 400) {
        throw new Error('Checkout request was rejected. Please verify booking details and try again.')
      }
      
      if (error.message.includes('401')) {
        throw new Error('Authentication required. Please log in and try again.');
      }
      
      if (error.message.includes('402')) {
        throw new Error('Payment declined. Please check your card details or try a different payment method.');
      }
      
      if (error.message.includes('500')) {
        throw new Error('Server error. Please try again in a few moments.');
      }
      
      throw error;
    }
  }

  // Alternative method if you need to wrap the data in a different structure
  async processFinalCheckoutWrapped(checkoutData) {

    try {
      // Wrap the checkout data if your API expects it
      const requestBody = {
        checkoutData: checkoutData
      };

      const response = await api.post('/checkout/process-final-checkout', requestBody);

     
      return response;

    } catch (error) {
      console.error('❌ Checkout error:', error);
      throw error;
    }
  }

  // Method to validate checkout data before sending
  validateCheckoutData(checkoutData) {
    const errors = [];

    if (!checkoutData.finalPriceUsd || checkoutData.finalPriceUsd <= 0) {
      errors.push('Invalid amount');
    }

    if (!checkoutData.cardDetails) {
      errors.push('Card details are required');
      return errors;
    }

    if (checkoutData.bookingReference) {
      const value = String(checkoutData.bookingReference.value || '').trim()
      const type = String(checkoutData.bookingReference.type || '').trim()
      if (!value || !type) {
        errors.push('bookingReference must include both value and type')
      }
    }

    const { cardDetails } = checkoutData;

    const hasStripeSavedMethod =
      !!(cardDetails.stripePaymentMethodId || cardDetails.id || cardDetails.cardId);
    if (!hasStripeSavedMethod) {
      errors.push('Saved Stripe payment method is required');
    }

    if (!cardDetails.billingAddress) {
      errors.push('Billing address is required');
      return errors;
    }

    const { billingAddress } = cardDetails;

    if (!billingAddress.street || billingAddress.street.trim().length < 5) {
      errors.push('Invalid street address');
    }

    if (!billingAddress.city || billingAddress.city.trim().length < 2) {
      errors.push('Invalid city');
    }

    if (!billingAddress.stateProvince || billingAddress.stateProvince.trim().length < 2) {
      errors.push('Invalid state/province');
    }

    if (!billingAddress.postalCode || billingAddress.postalCode.trim().length < 3) {
      errors.push('Invalid postal code');
    }

    if (!billingAddress.countryCode || billingAddress.countryCode.length !== 2) {
      errors.push('Invalid country code');
    }

    return errors;
  }

  // Method with built-in validation
  async processFinalCheckoutWithValidation(checkoutData) {

    // Validate data first
    const validationErrors = this.validateCheckoutData(checkoutData);
    if (validationErrors.length > 0) {
      throw new Error(`Validation failed: ${validationErrors.join(', ')}`);
    }

    try {
      const response = await api.post('/checkout/process-final-checkout', checkoutData);

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Payment processing failed');
      }
      return result;

    } catch (error) {
      console.error('❌ Checkout error:', error);
      throw error;
    }
  }
}

export default new CheckoutService();